import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  StatusCalculatorService,
  ITechStack,
} from '../../services/status-calculator.service';
import { StatusToCssClassPipe } from '../../pipes/status-to-css-class.pipe';
import { TitleFormatPipe } from '../../pipes/title-format.pipe';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { DateEolService } from '../../../services/eol/eol.service';
import { CommentsService } from '../../../services/comments/comments.service';
import {
  IFullProject,
  TableRow,
  ITechStackMatrix,
  TechnologyInfo,
} from '../../../models';

@Component({
  selector: 'app-tabla',
  imports: [
    CommonModule,
    FormsModule,
    StatusToCssClassPipe,
    TitleFormatPipe,
    DateFormatPipe,
  ],
  templateUrl: './tabla.component.html',
  styleUrls: ['./tabla.component.scss'],
})
export class TablaComponent implements OnInit {
  @Input() ec2Repositories: IFullProject[] = [];
  repositoriesArray: IFullProject[] = [];
  filteredRepositories: IFullProject[] = [];
  searchTerm = '';
  isLoading: boolean = true;

  hoveredRow: { projectIndex: number; techIndex: number } | null = null;
  tableData: TableRow[] = [];

  eolStack: ITechStackMatrix[] = [];

  // Modal properties
  isModalOpen: boolean = false;
  selectedProject: string = '';
  currentComment: string = '';
  projectComments: Map<string, string> = new Map();

  constructor(
    readonly _statusCalculatorService: StatusCalculatorService,
    readonly _eolService: DateEolService,
    readonly _commentsService: CommentsService,
    readonly router: Router,
  ) {}

  ngOnInit() {
    this.getEOLStack();
  }

  async getEOLStack(): Promise<void> {
    try {
      this.eolStack = await this._eolService.fetchEOLStack();
    } catch (error) {
      console.error(error);
    }
  }

  ngOnChanges() {
    if (Array.isArray(this.ec2Repositories)) {
      this.isLoading = false;
      this.repositoriesArray = this.ec2Repositories;
      this.filteredRepositories = [...this.ec2Repositories];
    }
  }

  /**
   * Filtra los repositorios basado en el término de búsqueda (nombre de proyecto o tecnología)
   */
  onSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredRepositories = [...this.repositoriesArray];
    } else {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredRepositories = this.repositoriesArray.filter((repo) => {
        // Buscar por nombre de proyecto
        const matchesProject = repo.project.toLowerCase().includes(searchLower);

        // Buscar por tecnología en frontend
        const matchesFrontTech =
          repo.frontend?.stack?.some((tech: TechnologyInfo) =>
            tech.tech?.toLowerCase().includes(searchLower),
          ) || false;

        // Buscar por tecnología en backend
        const matchesBackendTech =
          repo.backend?.stack?.some((tech: TechnologyInfo) =>
            tech.tech?.toLowerCase().includes(searchLower),
          ) || false;

        return matchesProject || matchesFrontTech || matchesBackendTech;
      });
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredRepositories = [...this.repositoriesArray];
  }

  onRowHover(
    projectIndex: number,
    techIndex: number,
    isHovered: boolean,
  ): void {
    this.hoveredRow = isHovered ? { projectIndex, techIndex } : null;
  }

  isProjectColumnHovered(projectIndex: number): boolean {
    return (
      this.hoveredRow !== null && this.hoveredRow.projectIndex === projectIndex
    );
  }

  navigateToProject(projectName: string): void {
    // Find the complete project in the data
    const projectData = this.filteredRepositories.find(
      (repo) => repo.project === projectName,
    );

    if (projectData) {
      // Navigate passing the complete object as state
      this.router.navigate(['/project', projectName], {
        state: { projectData: projectData },
      });
    } else {
      // Fallback if project is not found
      this.router.navigate(['/project', projectName]);
    }
  }

  getRowSpan(technologies: any[]): number {
    return technologies.length;
  }

  mapToITechStack(tech: TechnologyInfo, techName: string): ITechStack {
    return {
      tech: techName,
      support: tech.support,
      eol: tech.eol,
      extendedSupport: tech.extendedSupport,
    };
  }

  calculateTechStatus(
    tech: TechnologyInfo,
    techName: string,
    yearsAdvanced = 0,
  ): string {
    if (!tech || !techName) {
      return 'status-gray';
    }

    const finalTechName = techName || tech.tech || 'unknown';
    const techStack = this.mapToITechStack(tech, finalTechName);

    try {
      const status = this._statusCalculatorService.calculateStatus(
        techStack,
        yearsAdvanced,
      );
      return this._statusCalculatorService.getStatusCssClass(status);
    } catch (error) {
      console.warn('Error calculating tech status:', error);
      return 'status-gray';
    }
  }

  /**
   * Devuelve un array unificado (frontend primero, luego backend) para alinear correctamente
   * los items entre columnas usando un único *ngFor.
   */
  getCombinedStack(row: any): TechnologyInfo[] {
    if (!row) return [];

    const frontend = Array.isArray(row?.frontend?.stack)
      ? row.frontend.stack
      : [];
    const back = Array.isArray(row?.backend?.stack) ? row.backend.stack : [];

    // Ensure each item has a valid tech
    const combinedStack = [...frontend, ...back].map((item) => ({
      ...item,
      tech: item.tech || 'unknown',
    }));

    return combinedStack;
  }

  /**
   * Obtiene todas las tecnologías (frontend + backend) de un proyecto
   */
  getAllTechnologies(row: any): TechnologyInfo[] {
    const frontend = Array.isArray(row?.frontend?.stack)
      ? row.frontend.stack
      : [];
    const backend = Array.isArray(row?.backend?.stack) ? row.backend.stack : [];
    return [...frontend, ...backend];
  }

  getTotalTechnologies(row: any): number {
    return this.getAllTechnologies(row).length;
  }

  formatEolDate(eol: string | boolean | undefined): string | null {
    if (eol && typeof eol === 'string' && eol !== '') {
      return eol;
    }
    return null;
  }

  getRecommendedVersion(techName: string, currentVersion?: string): string {
    return this._eolService._getRecommendedVersion(
      techName,
      this.eolStack,
      currentVersion,
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  openCommentModal(projectName: string): void {
    this.selectedProject = projectName;
    this.currentComment = this.projectComments.get(projectName) || '';
    this.isModalOpen = true;

    // Cargar el contenido después de que el modal se abra
    setTimeout(() => {
      const editor = document.querySelector('.rich-text-editor') as HTMLElement;
      if (editor) {
        editor.innerHTML = this.currentComment;
      }
    }, 0);
  }

  closeCommentModal(): void {
    this.isModalOpen = false;
    this.selectedProject = '';
  }

  viewComments(projectName: string): void {
    // Find the complete project in the data
    const projectData = this.filteredRepositories.find(
      (repo) => repo.project === projectName,
    );

    if (projectData) {
      // Navigate passing the complete object and scrollToComments flag as state
      this.router.navigate(['/project', projectName], {
        state: { projectData: projectData, scrollToComments: true },
      });
    } else {
      // Fallback if project is not found
      this.router.navigate(['/project', projectName], {
        state: { scrollToComments: true },
      });
    }
  }

  async saveComment(): Promise<void> {
    if (this.selectedProject && this.currentComment) {
      try {
        // Guardar en el mapa local
        this.projectComments.set(this.selectedProject, this.currentComment);

        // Enviar al servicio
        const response = await this._commentsService.createComment(
          this.selectedProject,
          this.currentComment,
        );

        this.closeCommentModal();
      } catch (error) {
        console.error('Error saving comment:', error);
        // Podrías mostrar un mensaje de error al usuario aquí
      }
    } else {
      this.closeCommentModal();
    }
  }

  onEditorInput(event: Event): void {
    const element = event.target as HTMLElement;
    this.currentComment = element.innerHTML;
  }

  formatText(command: string): void {
    document.execCommand(command, false, undefined);
  }
}
